import { writable, derived, get } from 'svelte/store'
import type { GrammarError } from '$types/grammar'
import { ErrorMergeService } from '$lib/services/ErrorMergeService'
import { settingsActions } from './settings'

// Type definitions for the store states
export interface TextState {
  original: string      // Original text from user input
  current: string       // Current working text (being edited)
  corrected: string     // Text with all corrections applied
  preview: string       // Preview text for mini-editor
}

export interface ErrorState {
  errors: GrammarError[]
  ignoredErrors: Set<string>           // Error IDs that user has ignored
  appliedCorrections: Map<string, string>  // errorId -> correctionId mapping
  activeErrorId: string | null         // Currently highlighted/selected error
}

export interface GrammarStats {
  totalErrors: number
  errorCount: number      // Level: error
  warningCount: number    // Level: warning
  suggestionCount: number // Level: suggestion
  ignoredCount: number
  appliedCount: number
  characterCount: number
  wordCount: number
  hasErrors: boolean
  hasActiveErrors: boolean // Has non-ignored errors
}

// Create the writable stores
export const textStore = writable<TextState>({
  original: '',
  current: '',
  corrected: '',
  preview: ''
})

export const errorStore = writable<ErrorState>({
  errors: [],
  ignoredErrors: new Set(),
  appliedCorrections: new Map(),
  activeErrorId: null
})

// Derived store for statistics
export const statsStore = derived(
  [textStore, errorStore],
  ([$textStore, $errorStore]) => {
    const errors = $errorStore.errors
    
    // Filter out ignored errors and errors where original is kept
    const activeErrors = errors.filter(e => 
      !$errorStore.ignoredErrors.has(e.id) && 
      e.userAction !== 'kept-original' &&
      !e.isOriginalKept
    )
    
    // Count only active errors (not ignored, not kept-original)
    const activeErrorCount = activeErrors.filter(e => e.level === 'error').length
    const activeWarningCount = activeErrors.filter(e => e.level === 'warning').length
    const activeSuggestionCount = activeErrors.filter(e => e.level === 'suggestion').length
    
    // Text statistics
    const text = $textStore.current || $textStore.original || ''
    const characterCount = text.length
    
    // Count Korean words (space-separated)
    const wordCount = text
      .split(/\s+/)
      .filter(word => word.length > 0).length
    
    return {
      totalErrors: activeErrors.length,  // Only count active errors
      errorCount: activeErrorCount,
      warningCount: activeWarningCount,
      suggestionCount: activeSuggestionCount,
      ignoredCount: $errorStore.ignoredErrors?.size || 0,
      appliedCount: $errorStore.appliedCorrections?.size || 0,
      characterCount,
      wordCount,
      hasErrors: activeErrors.length > 0,
      hasActiveErrors: activeErrors.length > 0
    } as GrammarStats
  }
)

// Action functions for store manipulation
export const grammarActions = {
  // Text actions
  setText(text: string) {
    textStore.update(state => ({
      ...state,
      original: text,
      current: text
    }))
  },
  
  updateCurrentText(text: string) {
    textStore.update(state => ({
      ...state,
      current: text
    }))
  },
  
  setCorrectedText(text: string) {
    textStore.update(state => ({
      ...state,
      corrected: text
    }))
  },
  
  setPreviewText(text: string) {
    textStore.update(state => ({
      ...state,
      preview: text
    }))
  },
  
  // Error actions
  setErrors(errors: GrammarError[], mergeWithAI = true) {
    errorStore.update(state => {
      let finalErrors = errors;
      
      
      // If we should merge with existing errors
      if (mergeWithAI) {
        // Check if new errors are AI errors (all have isAIOnly flag)
        const areNewErrorsAI = errors.length > 0 && errors.every(e => e.isAIOnly || e.isAIGenerated);
        
        if (areNewErrorsAI) {
          // New errors are AI errors - keep existing grammar errors
          const existingGrammarErrors = state.errors.filter(e => !e.isAIOnly && !e.isAIGenerated);
          
          
          if (existingGrammarErrors.length > 0) {
            // Merge: existing grammar errors + new AI errors
            finalErrors = ErrorMergeService.deduplicateErrors(existingGrammarErrors, errors);
          }
        } else {
          // New errors are grammar errors - keep existing AI errors
          const existingAIErrors = state.errors.filter(e => e.isAIOnly || e.isAIGenerated);
          
          
          if (existingAIErrors.length > 0) {
            // Merge: existing AI errors + new grammar errors
            finalErrors = ErrorMergeService.deduplicateErrors(existingAIErrors, errors);
          }
        }
      }
      
      // Auto-open sidebar when errors are found
      if (finalErrors.length > 0) {
        settingsActions.updateUI({ showSidebar: true });
      }
      
      return {
        ...state,
        errors: finalErrors,
        // Clear applied corrections for errors that no longer exist
        appliedCorrections: new Map(
          Array.from(state.appliedCorrections).filter(([errorId]) =>
            finalErrors.some(e => e.id === errorId)
          )
        )
      };
    });
  },
  
  ignoreError(errorId: string) {
    errorStore.update(state => {
      const ignoredErrors = new Set(state.ignoredErrors)
      ignoredErrors.add(errorId)
      
      // Also update the error's status if it was kept-original
      const errors = state.errors.map(error => {
        if (error.id === errorId && (error.isOriginalKept || error.userAction === 'kept-original')) {
          return {
            ...error,
            isOriginalKept: false,
            userAction: 'ignored' as const
          }
        }
        return error
      })
      
      return {
        ...state,
        errors,
        ignoredErrors
      }
    })
  },
  
  unignoreError(errorId: string) {
    errorStore.update(state => {
      const ignoredErrors = new Set(state.ignoredErrors)
      ignoredErrors.delete(errorId)
      return {
        ...state,
        ignoredErrors
      }
    })
  },
  
  setIgnoredErrors(ignoredErrorIds: Set<string>) {
    errorStore.update(state => ({
      ...state,
      ignoredErrors: new Set(ignoredErrorIds)
    }))
  },
  
  clearIgnoredErrors() {
    errorStore.update(state => ({
      ...state,
      ignoredErrors: new Set()
    }))
  },
  
  applyCorrection(errorId: string, correctionId: string) {
    errorStore.update(state => {
      const appliedCorrections = new Map(state.appliedCorrections)
      appliedCorrections.set(errorId, correctionId)
      return {
        ...state,
        appliedCorrections
      }
    })
  },
  
  removeAppliedCorrection(errorId: string) {
    errorStore.update(state => {
      const appliedCorrections = new Map(state.appliedCorrections)
      appliedCorrections.delete(errorId)
      return {
        ...state,
        appliedCorrections
      }
    })
  },
  
  setActiveError(errorId: string | null) {
    errorStore.update(state => ({
      ...state,
      activeErrorId: errorId
    }))
  },
  
  clearActiveError() {
    errorStore.update(state => ({
      ...state,
      activeErrorId: null
    }))
  },
  
  // Complex actions
  applyAllCorrections(): string {
    const { errors, appliedCorrections } = get(errorStore)
    const { current, original } = get(textStore)
    const text = current || original
    
    // Sort errors by position (reverse order to apply from end to start)
    const sortedErrors = [...errors]
      .filter(error => error.corrections.length > 0)
      .sort((a, b) => (b.position?.start || 0) - (a.position?.start || 0))
    
    let correctedText = text
    
    for (const error of sortedErrors) {
      // Skip ignored errors
      if (get(errorStore).ignoredErrors.has(error.id)) continue
      
      // Find the correction to apply (either user-selected or first suggestion)
      const correctionId = appliedCorrections.get(error.id) || error.corrections[0]?.id
      const correction = error.corrections.find(c => c.id === correctionId)
      
      if (correction) {
        // Apply the correction
        correctedText = 
          correctedText.slice(0, error.position?.start || 0) +
          correction.text +
          correctedText.slice(error.position?.end || 0)
        
        // Track the applied correction
        grammarActions.applyCorrection(error.id, correction.id)
      }
    }
    
    // Update the corrected text in the store
    grammarActions.setCorrectedText(correctedText)
    
    return correctedText
  },
  
  // Reset all stores
  reset() {
    textStore.set({
      original: '',
      current: '',
      corrected: '',
      preview: ''
    })
    
    errorStore.set({
      errors: [],
      ignoredErrors: new Set(),
      appliedCorrections: new Map(),
      activeErrorId: null
    })
  }
}

// Export a subscribe function for the combined state
export function subscribeToGrammarState(callback: (state: {
  text: TextState,
  errors: ErrorState,
  stats: GrammarStats
}) => void) {
  const unsubscribers: Array<() => void> = []
  
  // Subscribe to all stores
  unsubscribers.push(
    textStore.subscribe(text => {
      callback({
        text,
        errors: get(errorStore),
        stats: get(statsStore)
      })
    })
  )
  
  unsubscribers.push(
    errorStore.subscribe(errors => {
      callback({
        text: get(textStore),
        errors,
        stats: get(statsStore)
      })
    })
  )
  
  // Return unsubscribe function
  return () => {
    unsubscribers.forEach(unsub => unsub())
  }
}